import { c as _c } from "react/compiler-runtime";
import { IcoChevronRight16, IcoRobot16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import useDocumentsPanelOpen from '../../hooks/use-documents-panel-open';
import { type BackgroundAgents, useBackgroundAgents } from '../chat-widget/background-agents-context';
type RunningAgentsIndicatorProps = {
  /** Override the store — for stories/tests. */
  agents?: BackgroundAgents;
  /** `pill` (default) is the standalone entry above the composer; `footer` is the row grouped
   *  inside the pinned card, below the pinned content, when a card is already pinned. */
  variant?: 'pill' | 'footer';
};

// Persistent entry to the Background-agents sidebar. Shows the running count while live, the total
// once idle; hidden when there are no agents.
const RunningAgentsIndicator = t0 => {
  const $ = _c(33);
  const {
    agents,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "pill" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "chat.backgroundAgents"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("copilot", t2);
  const {
    openTo
  } = useDocumentsPanelOpen();
  const stored = useBackgroundAgents();
  const items = (agents ?? stored).items;
  let t3;
  if ($[1] !== items) {
    t3 = items.filter(_temp);
    $[1] = items;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const running = t3.length;
  if (items.length === 0) {
    return null;
  }
  const live = running > 0;
  let t4;
  if ($[3] !== items.length || $[4] !== live || $[5] !== running || $[6] !== t) {
    t4 = live ? running === 1 ? t(_temp2, {
      count: 1
    }) : t(_temp3, {
      count: running
    }) : items.length === 1 ? t(_temp4, {
      count: 1
    }) : t(_temp5, {
      count: items.length
    });
    $[3] = items.length;
    $[4] = live;
    $[5] = running;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const label = t4;
  if (variant === "footer") {
    let t5;
    if ($[8] !== openTo) {
      t5 = () => openTo("agents");
      $[8] = openTo;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    const t6 = live ? "bg-accent" : "bg-quaternary";
    let t7;
    if ($[10] !== t6) {
      t7 = cx("shrink-0 size-1.5 rounded-full", t6);
      $[10] = t6;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== t7) {
      t8 = <span aria-hidden={true} className={t7} />;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <IcoRobot16 className="shrink-0 text-tertiary" />;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t10;
    if ($[15] !== label) {
      t10 = <span className="flex-1 min-w-0 text-label-3 text-primary truncate">{label}</span>;
      $[15] = label;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <IcoChevronRight16 className="shrink-0 text-quaternary group-hover:text-tertiary" />;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    let t12;
    if ($[18] !== t10 || $[19] !== t5 || $[20] !== t8) {
      t12 = <button type="button" onClick={t5} className="group shrink-0 -mx-3 -mb-3 flex items-center gap-2 rounded-b-default px-3 py-2 text-left hover:bg-hover transition-colors cursor-pointer">{t8}{t9}{t10}{t11}</button>;
      $[18] = t10;
      $[19] = t5;
      $[20] = t8;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    return t12;
  }
  let t5;
  if ($[22] !== openTo) {
    t5 = () => openTo("agents");
    $[22] = openTo;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const t6 = live ? "bg-accent" : "bg-quaternary";
  let t7;
  if ($[24] !== t6) {
    t7 = cx("size-1.5 rounded-full", t6);
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t7) {
    t8 = <span aria-hidden={true} className={t7} />;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <IcoChevronRight16 className="text-quaternary group-hover:text-tertiary" />;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== label || $[30] !== t5 || $[31] !== t8) {
    t10 = <button type="button" onClick={t5} className="group flex items-center gap-1.5 self-start rounded-full border border-solid border-tertiary bg-primary py-1 pl-2 pr-1.5 text-caption-1 text-secondary hover:bg-hover-control cursor-pointer">{t8}{label}{t9}</button>;
    $[29] = label;
    $[30] = t5;
    $[31] = t8;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
};
export default RunningAgentsIndicator;
function _temp(agent) {
  return agent.running;
}
function _temp2($) {
  return $.indicator_one;
}
function _temp3($_0) {
  return $_0.indicator_other;
}
function _temp4($_1) {
  return $_1.indicatorFinished_one;
}
function _temp5($_2) {
  return $_2.indicatorFinished_other;
}